import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import StepCardHeader from '../Components/StepCardHeaders'
import { CInputCheckbox, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import { API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE } from '../../../../../../constants';
import { toast } from 'react-toastify';
import { callTokenApi } from '../../../../../../apiCaller';
import { useSelector } from 'react-redux';
import { ConfirmSaveChange } from '../../../../../general/popup';
import FormikStateObserver from '../Components/FormikStateObserver';
import isEqual from 'lodash/isEqual';
import Waiting from '../../../../../general/Loadings/Waiting';
import ToastEditor from '../../../../../general/toastEditor';

const FormGroup = ({
    name,
    label,
    errors,
    touched,
    values,
    ...props
}) => {
    return (
        <CFormGroup className="mb-3">
            {label && <CLabel htmlFor={name}>{label}</CLabel>}
            <CTextarea
                id={name}
                invalid={!!errors[name] && touched[name]}
                value={values[name]}
                className="py-2 px-3 lh-base overflow-auto scrollbar-bar-custom"
                rows={4}
                {...props}
            />

            <CInvalidFeedback>
                <ErrorMessage name={name} />
            </CInvalidFeedback>
        </CFormGroup>
    );
}

const FormikStateSyncAndDirtyReporter = memo(({
    stepId,
    currentFormValues,
    savedStepData,
    updateUnsavedEdits,
    handleSetUnsavedChanges
}) => {
    const previousValuesForCacheRef = useRef(currentFormValues);
    const stringifiedCurrentFormValues = useMemo(() => JSON.stringify(currentFormValues), [currentFormValues]);
    const stringifiedSavedStepData = useMemo(() => JSON.stringify(savedStepData), [savedStepData]);

    useEffect(() => {
        if (!isEqual(currentFormValues, previousValuesForCacheRef.current)) {
            updateUnsavedEdits(stepId, currentFormValues);
            previousValuesForCacheRef.current = currentFormValues;
        }

        const isDirtyComparedToSaved = !isEqual(currentFormValues, savedStepData);
        handleSetUnsavedChanges(isDirtyComparedToSaved);
    }, [
        stepId,
        stringifiedCurrentFormValues,
        stringifiedSavedStepData,
        updateUnsavedEdits,
        handleSetUnsavedChanges,
        currentFormValues,
        savedStepData
    ]);

    return null;
});

const Instructions = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        unsavedEdits,
        updateUnsavedEdits,
        clearUnsavedEditsForStep,
        handleSetUnsavedChanges,
        clearUnsavedChangesFlag,
    } = useContext(AIInsightsDetailContext);

    const setFieldValueRef = useRef(null);
    const [data, setData] = useState({});
    const [showToggle, setShowToggle] = useState(false);
    const intelligenceSetting = useSelector((state) => state.subscriber.intelligenceSetting);

    const getRelevantStepFields = useCallback((sourceData) => ({
        mainInstructions: sourceData?.mainInstructions?.trim() || '',
        businessDescription: sourceData?.businessDescription?.trim() || '',
        isBusinessDescriptionExcluded: sourceData?.isBusinessDescriptionExcluded || false,
        isBusinessDescriptionCustom: sourceData?.isBusinessDescriptionCustom || false,
        customerProfile: sourceData?.customerProfile?.trim() || '',
        isCustomerProfileExcluded: sourceData?.isCustomerProfileExcluded || false,
        isCustomerProfileCustom: sourceData?.isCustomerProfileCustom || false,
        rulesAndFilters: sourceData?.rulesAndFilters?.trim() || '',
        isRulesAndFiltersExcluded: sourceData?.isRulesAndFiltersExcluded || false,
    }), []);

    const initialValues = useMemo(() => {
        const base = getRelevantStepFields(stepsData);
        const defaultsFromSettings = {
            businessDescription: base.businessDescription || intelligenceSetting?.businessDescription?.trim() || '',
            customerProfile: base.customerProfile || intelligenceSetting?.customerProfile?.trim() || '',
        };
        const combinedWithSettings = { ...base, ...defaultsFromSettings };
        const unsavedForThisStep = unsavedEdits[activeStep] || {};
        return { ...combinedWithSettings, ...unsavedForThisStep };
    }, [stepsData, unsavedEdits, intelligenceSetting, getRelevantStepFields, activeStep]);

    const savedStepData = useMemo(() => {
        const base = getRelevantStepFields(stepsData);
        const defaultsFromSettings = {
             businessDescription: base.businessDescription || intelligenceSetting?.businessDescription?.trim() || '',
             customerProfile: base.customerProfile || intelligenceSetting?.customerProfile?.trim() || '',
        };
        return { ...base, ...defaultsFromSettings };
    }, [stepsData, intelligenceSetting, getRelevantStepFields]);

    const validationSchema = Yup.object().shape({
        mainInstructions: Yup.string().required('Primary Instruction is required'),
    
        businessDescription: Yup.string().when(
            // Check both flags: isBusinessDescriptionCustom and isBusinessDescriptionExcluded
            'isBusinessDescriptionExcluded',
            {
                is: (isExcluded) => isExcluded === false,
                then: (schema) => schema.required('Business Description is required when customized and not excluded'),
                otherwise: (schema) => schema.nullable(), // Or just schema if empty string is acceptable
            }
        ),
    
        customerProfile: Yup.string().when(
            // Check both flags: isCustomerProfileCustom and isCustomerProfileExcluded
            'isCustomerProfileExcluded',
            {
                is: (isExcluded) => isExcluded === false,
                then: (schema) => schema.required('Customer Profile is required when customized and not excluded'),
                otherwise: (schema) => schema.nullable(),
            }
        ),
    
        // Assuming rulesAndFilters might also have an exclude flag
        rulesAndFilters: Yup.string().when(
            'isRulesAndFiltersExcluded', // Check the exclude flag
            {
                is: (isExcluded) => isExcluded === false,
                then: (schema) => schema.required('Rules are required when not excluded'),
                otherwise: (schema) => schema.nullable(),
            }
        ),
    
        // Include the boolean flags themselves if they need validation (usually not needed)
        isBusinessDescriptionExcluded: Yup.boolean(),
        isBusinessDescriptionCustom: Yup.boolean(),
        isCustomerProfileExcluded: Yup.boolean(),
        isCustomerProfileCustom: Yup.boolean(),
        isRulesAndFiltersExcluded: Yup.boolean(), // Assuming this exists
    });

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const handleResetDefault = (fieldInfo, setFieldValue) => {
        setShowToggle(true);
        setData(fieldInfo);

        setFieldValueRef.current = setFieldValue;
    }

    const onAccept = () => {
        const { type, valueFieldName } = data;
        const setFieldValue = setFieldValueRef.current;

        if (!setFieldValue) return;

        const defaultValue = intelligenceSetting?.[valueFieldName]?.trim() || '';
      
        setFieldValue(valueFieldName, defaultValue);
        if (type === 'checked') setFieldValue(`is${capitalize(valueFieldName)}Custom`, false);
        setShowToggle(false);
        setData({});
    }
    
    const onSubmit = useCallback(
        async values => {
            const {
                mainInstructions,
                businessDescription,
                isBusinessDescriptionExcluded,
                isBusinessDescriptionCustom,
                customerProfile,
                isCustomerProfileExcluded,
                isCustomerProfileCustom,
                rulesAndFilters,
                isRulesAndFiltersExcluded,
            } = values;

            const currentStep = 3;

            const trimmedValues = {
                mainInstructions: mainInstructions?.trim() || '',
                businessDescription: businessDescription?.trim() || '',
                isBusinessDescriptionExcluded,
                isBusinessDescriptionCustom,
                customerProfile: customerProfile?.trim() || '',
                isCustomerProfileExcluded,
                isCustomerProfileCustom,
                rulesAndFilters: rulesAndFilters?.trim() || '',
                isRulesAndFiltersExcluded,
                lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep
                    ? stepsData.lastEditStep
                    : currentStep,
            };
            
            const hasChanges = Object.keys(trimmedValues).some((key) => {
                return trimmedValues[key] !== stepsData[key];
            });

            if (!hasChanges) {
                setActiveStep(4);
                return;
            }

            try {
                const response = await callTokenApi(
                    `${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${stepsData.id}`,
                    'PUT',
                    trimmedValues
                );
            
                if (response?.status === 200 || response?.data?.code === 200) {
                    setStepsData({
                        ...stepsData,
                        ...trimmedValues,
                    });
                
                    toast.success('Instructions updated successfully.');
                    clearUnsavedEditsForStep(activeStep);
                    clearUnsavedChangesFlag();
                    setActiveStep(4);
                } else {
                    toast.error('Failed to update instructions. Please try again.');
                }
            } catch (error) {
                console.error('Instruction update error:', error);
                toast.error('Something went wrong while saving. Please try again later.');
            }
        },
        [stepsData, setStepsData, setActiveStep, activeStep, clearUnsavedEditsForStep, clearUnsavedChangesFlag]
    );

    return (
        <StepCardHeader
            title={`Instructions`}
            step={3}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(3)}
        >
            <p>Provide additional context and instructions for the analysis you'd like generated by the AI model. </p>

            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                enableReinitialize
                validationSchema={validationSchema}
            >
                {({ values, setFieldValue, errors, touched, dirty, isSubmitting, handleSubmit, handleChange }) => {
                    return (
                        <>
                            <FormikStateObserver dirty={dirty} onDirtyChange={handleSetUnsavedChanges} />
                            <FormikStateSyncAndDirtyReporter
                                stepId={activeStep}
                                currentFormValues={getRelevantStepFields(values)}
                                savedStepData={savedStepData}
                                updateUnsavedEdits={updateUnsavedEdits}
                                handleSetUnsavedChanges={handleSetUnsavedChanges}
                            />

                            <CForm onSubmit={handleSubmit} noValidate>
                                <div className='mb-4'>
                                    <h3 className='f-15'>Primary Instruction</h3>

                                    <p className='text-muted f-13'>
                                        Provide a written description of what you'd like the AI Model to do during it's analysis. Keep it as simple as possible. The context of about your request will be generated primarily from the data descriptions (previous step) and example ouputs (next step). We have provided an example set of instructions below. Modify these instructions to match your situation. Consider including some sort of time-based reference in your instructions to clarify the data available to the model. 
                                    </p>

                                    <div className={`${errors.mainInstructions && touched.mainInstructions ? 'is-invalid' : ''}`}>
                                            <ToastEditor
                                                id="mainInstructions"
                                                initialValue={values.mainInstructions}
                                                onChange={(content) => setFieldValue('mainInstructions', content)}
                                            />
                                    </div>

                                    <CInvalidFeedback>
                                        <ErrorMessage name="mainInstructions" />
                                    </CInvalidFeedback>
                                </div>

                                <div className='mb-4'>
                                    <CRow className="customer-profile-row">
                                        <CCol lg="8">
                                            <h3 className='f-15'>Business Description</h3>

                                            <p className='text-muted f-13'>
                                                This is the universal business description you've provided ListenLayer under the Intelligent Objective. You can include or exclude this for context, and you can also customize it for this AI Template.
                                            </p>

                                            <FormGroup
                                                name="businessDescription"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                disabled={!values.isBusinessDescriptionCustom || values.isBusinessDescriptionExcluded}
                                                onChange={handleChange}
                                                placeholder="Enter Business Description"
                                                className='input-ai-insights'
                                            />
                                        </CCol>

                                        <CCol lg="4" className="d-flex align-items-end actions-column">
                                            <CFormGroup variant="custom-checkbox" inline>
                                                <CInputCheckbox
                                                    custom
                                                    id='isBusinessDescriptionExcluded'
                                                    name='isBusinessDescriptionExcluded'
                                                    className="m-auto"
                                                    checked={values.isBusinessDescriptionExcluded}
                                                    onChange={handleChange}
                                                />
                                                <CLabel htmlFor='isBusinessDescriptionExcluded' variant="custom-checkbox">Exclude</CLabel>
                                            </CFormGroup>

                                            <CFormGroup variant="custom-checkbox" inline>
                                                <CInputCheckbox
                                                    custom
                                                    id='isBusinessDescriptionCustom'
                                                    name='isBusinessDescriptionCustom'
                                                    className="m-auto"
                                                    checked={values.isBusinessDescriptionCustom}
                                                    onChange={(e) => {
                                                        if (!e.target.checked) {
                                                            handleResetDefault({
                                                                displayName: 'Business Description',
                                                                valueFieldName: 'businessDescription',
                                                                type: 'checked',
                                                            }, setFieldValue);
                                                            return;
                                                        }

                                                        setFieldValue('isBusinessDescriptionCustom', e.target.checked);
                                                    }}
                                                />
                                                <CLabel htmlFor='isBusinessDescriptionCustom' variant="custom-checkbox">
                                                    Customize this value
                                                </CLabel>
                                            </CFormGroup>
                                        
                                            {values.isBusinessDescriptionCustom && (
                                                <CButton
                                                    type="button"
                                                    className="mb-1 btn-sm text-underline text-capitalize"
                                                    color="link"
                                                    onClick={() => handleResetDefault({
                                                        displayName: 'Business Description',
                                                        valueFieldName: 'businessDescription',
                                                        type: 'button',
                                                    }, setFieldValue)}
                                                >
                                                    Reset to default
                                                </CButton>
                                            )}
                                        </CCol>
                                    </CRow>
                                </div>

                                <div className='mb-4'>
                                    <CRow className="customer-profile-row" >
                                        <CCol lg="8">
                                            <h3 className='f-15'>Customer Profile</h3>

                                            <p className='text-muted f-13'>
                                                This is the universal customer profile description you've provided ListenLayer under the Intelligent Objective. You can include or exclude this for context, and you can also customize it for this AI Template.
                                            </p>

                                            <FormGroup
                                                name="customerProfile"
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                disabled={!values.isCustomerProfileCustom || values.isCustomerProfileExcluded}
                                                onChange={handleChange}
                                                placeholder="Enter Customer Profile"
                                                className='input-ai-insights'
                                            />
                                        </CCol>

                                        <CCol lg="4" className="d-flex align-items-end actions-column">
                                            <CFormGroup variant="custom-checkbox" inline>
                                                <CInputCheckbox
                                                    custom
                                                    id='isCustomerProfileExcluded'
                                                    name='isCustomerProfileExcluded'
                                                    className="m-auto"
                                                    checked={values.isCustomerProfileExcluded}
                                                    onChange={handleChange}
                                                />
                                                <CLabel htmlFor='isCustomerProfileExcluded' variant="custom-checkbox">Exclude</CLabel>
                                            </CFormGroup>

                                            <CFormGroup variant="custom-checkbox" inline>
                                                <CInputCheckbox
                                                    custom
                                                    id='isCustomerProfileCustom'
                                                    name='isCustomerProfileCustom'
                                                    className="m-auto"
                                                    checked={values.isCustomerProfileCustom}
                                                    onChange={(e) => {
                                                        if (!e.target.checked) {
                                                            handleResetDefault({
                                                                displayName: 'Customer Profile',
                                                                valueFieldName: 'customerProfile',
                                                                type: 'checked',
                                                            }, setFieldValue);
                                                            return;
                                                        }

                                                        setFieldValue('isCustomerProfileCustom', e.target.checked);
                                                    }}
                                                />
                                                <CLabel htmlFor='isCustomerProfileCustom' variant="custom-checkbox">
                                                    Customize this value
                                                </CLabel>
                                            </CFormGroup>
                                        
                                            {values.isCustomerProfileCustom && (
                                                <CButton
                                                    type="button"
                                                    className="mb-1 btn-sm text-underline text-capitalize"
                                                    color="link"
                                                    onClick={() => handleResetDefault({
                                                        displayName: 'Customer Profile',
                                                        valueFieldName: 'customerProfile',
                                                        type: 'button',
                                                    }, setFieldValue)}
                                                >
                                                    Reset to default
                                                </CButton>
                                            )}
                                        </CCol>
                                    </CRow>
                                </div>

                                <div className='mb-4'>
                                    <CRow>
                                        <CCol lg="8">
                                            <h3 className='f-15'>Rules & Filters</h3>

                                            <p className='text-muted f-13'>
                                                Write a bulletted list of rules or filters that you want the analysis to strictly adhere to. This is optional. 
                                            </p>

                                            <div className={`${errors.rulesAndFilters && touched.rulesAndFilters ? 'is-invalid' : ''}`}>
                                                <ToastEditor
                                                    id="rulesAndFilters"
                                                    initialValue={values.rulesAndFilters}
                                                    disabledChange={!!values.isRulesAndFiltersExcluded}
                                                    onChange={(content) => setFieldValue('rulesAndFilters', content)}
                                                />
                                            </div>

                                            <CInvalidFeedback>
                                                <ErrorMessage name="rulesAndFilters" />
                                            </CInvalidFeedback>
                                        </CCol>
                                      
                                        <CCol lg="4" className="d-flex align-items-end">
                                            <CFormGroup variant="custom-checkbox" inline>
                                                <CInputCheckbox
                                                    custom
                                                    id='isRulesAndFiltersExcluded'
                                                    name='isRulesAndFiltersExcluded'
                                                    className="m-auto"
                                                    checked={values.isRulesAndFiltersExcluded}
                                                    onChange={handleChange}
                                                />
                                                <CLabel htmlFor='isRulesAndFiltersExcluded' variant="custom-checkbox">Exclude</CLabel>
                                            </CFormGroup>
                                        </CCol>
                                    </CRow>
                                </div>

                                <CButton disabled={isSubmitting} type="submit" className="px-4" color="primary">
                                    <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                                </CButton>
                            </CForm>
                        </>
                    );
                }}
            </Formik>

            {showToggle && (
                <ConfirmSaveChange
                    show={showToggle}
                    onAccept={onAccept}
                    onClose={() => {
                        setShowToggle(false);
                        setData({});
                    }}
                    title="Are you sure?"
                >
                    <p>
                        You're about to reset the <strong>{data.displayName}</strong> field to the default value from system settings.
                    </p>
                </ConfirmSaveChange>
            )}
        </StepCardHeader>
    );
};

export default Instructions;
